/**
 * Расчёт платной стоимости модификаторов с учётом free_quantity (iiko freeQuantity).
 *
 * Контракт 0 vs null (с бэка):
 * - `null` / `undefined` — ограничения нет (поле не задано)
 * - `0` — явно не бесплатный (групповой пул не применяется / личный кап = 0)
 * - `n > 0` — до n бесплатных единиц
 *
 * Группа:
 * - `free_quantity > 0` — общий пул на группу; бесплатными становятся самые дорогие единицы
 * - `free_quantity === 0` — в группе никто не бесплатен, даже если у пункта свой кап
 * - `free_quantity == null` — группового пула нет; действует только личный кап пункта (`n > 0`)
 *
 * Пункт:
 * - `0` — этот модификатор не может быть бесплатным
 * - `null` — личного капа нет (при групповом пуле может занять любые слоты пула)
 * - `n` — не больше n бесплатных единиц этого модификатора
 *
 * Если бэк при `childModifiersHaveMinMaxRestrictions: false` пришлёт детям `0`,
 * а не `null`, дети не попадут в групповой пул. Ожидаем `null` у детей в этом режиме.
 */

export type FreeQuantity = number | null | undefined;

export interface FreeModifierInput {
	id: string;
	price?: number | null;
	old_price?: number | null;
	total_new_price?: number | null;
	amount?: number | null;
	free_quantity?: FreeQuantity;
}

export interface FreeModifierBreakdown {
	id: string;
	amount: number;
	unitPrice: number;
	freeAmount: number;
	paidAmount: number;
	paidTotal: number;
}

export type ModifierPriceKey = "price" | "old_price";

const toAmount = (value: number | null | undefined): number => {
	const amount = Math.floor(Number(value) || 0);
	return amount > 0 ? amount : 0;
};

const toPrice = (value: number | null | undefined): number => {
	const price = Number(value);
	return Number.isFinite(price) ? price : 0;
};

/** Личный кап: 0 → нельзя бесплатно, null → без капа, n → до n единиц. */
export const getPersonalFreeCap = (freeQuantity: FreeQuantity): number => {
	if (freeQuantity === 0) return 0;
	if (freeQuantity == null) return Number.POSITIVE_INFINITY;
	const cap = Math.floor(Number(freeQuantity) || 0);
	return cap > 0 ? cap : 0;
};

export const allocateFreeModifiers = (
	groupFreeQuantity: FreeQuantity,
	modifiers: FreeModifierInput[],
	priceKey: ModifierPriceKey = "price",
): FreeModifierBreakdown[] => {
	const items = modifiers.map((modifier) => ({
		id: modifier.id,
		amount: toAmount(modifier.amount),
		unitPrice: toPrice(modifier[priceKey] ?? modifier.price),
		personalCap: getPersonalFreeCap(modifier.free_quantity),
	}));

	const groupIsBlocked = groupFreeQuantity === 0;
	const hasGroupPool = groupFreeQuantity != null && groupFreeQuantity > 0;
	let remainingPool = hasGroupPool ? Math.floor(Number(groupFreeQuantity) || 0) : 0;

	const freeById = new Map<string, number>();
	const ranked = items
		.map((item, index) => ({ item, index }))
		.filter(({ item }) => item.amount > 0)
		.sort((a, b) => b.item.unitPrice - a.item.unitPrice || a.index - b.index);

	for (const { item } of ranked) {
		if (groupIsBlocked) {
			freeById.set(item.id, 0);
			continue;
		}

		let freeAmount: number;
		if (hasGroupPool) {
			freeAmount = Math.min(item.amount, item.personalCap, remainingPool);
			remainingPool -= freeAmount;
		} else {
			freeAmount = item.personalCap === Number.POSITIVE_INFINITY ? 0 : Math.min(item.amount, item.personalCap);
		}

		freeById.set(item.id, freeAmount);
	}

	return items.map((item) => {
		const freeAmount = item.amount > 0 ? (freeById.get(item.id) ?? 0) : 0;
		const paidAmount = item.amount - freeAmount;

		return {
			id: item.id,
			amount: item.amount,
			unitPrice: item.unitPrice,
			freeAmount,
			paidAmount,
			paidTotal: paidAmount * item.unitPrice,
		};
	});
};

export const getPaidModifiersTotal = (groupFreeQuantity: FreeQuantity, modifiers: FreeModifierInput[], priceKey: ModifierPriceKey = "price"): number =>
	allocateFreeModifiers(groupFreeQuantity, modifiers, priceKey).reduce((sum, item) => sum + item.paidTotal, 0);

export interface SelectedModifierGroupInput {
	group?: string;
	id?: string;
	free_quantity?: FreeQuantity;
	modifiers?: FreeModifierInput[];
}

export interface ModifierGroupDefInput {
	id?: string;
	free_quantity?: FreeQuantity;
}

export const resolveGroupFreeQuantity = (selected: SelectedModifierGroupInput, groupDefs?: ModifierGroupDefInput[]): FreeQuantity => {
	if (selected.free_quantity !== undefined) return selected.free_quantity;
	const groupId = selected.group ?? selected.id;
	if (!groupId || !groupDefs?.length) return null;
	return groupDefs.find((group) => group.id === groupId)?.free_quantity ?? null;
};

export const getGroupsPaidTotal = (
	selectedGroups: SelectedModifierGroupInput[] | null | undefined,
	groupDefs?: ModifierGroupDefInput[],
	priceKey: ModifierPriceKey = "price",
): number =>
	(selectedGroups ?? []).reduce((sum, group) => {
		return sum + getPaidModifiersTotal(resolveGroupFreeQuantity(group, groupDefs), group.modifiers ?? [], priceKey);
	}, 0);

export const getCardSelectedModifiersPaidTotal = (
	state: { groupModifiers?: ModifierGroupDefInput[]; groupModifiersSelected?: SelectedModifierGroupInput[] } | null | undefined,
	priceKey: ModifierPriceKey = "price",
): number => getGroupsPaidTotal(state?.groupModifiersSelected, state?.groupModifiers, priceKey);

export interface CartModifiersInput {
	modifiers?: FreeModifierInput[];
	group_modifiers?: SelectedModifierGroupInput[];
}

export const getCartItemModifiersPaidTotal = (item: CartModifiersInput | null | undefined, priceKey: ModifierPriceKey = "price"): number => {
	if (!item) return 0;

	if (priceKey !== "old_price") {
		const allModifiers = [...(item.modifiers ?? []), ...(item.group_modifiers ?? []).flatMap((group) => group.modifiers ?? [])];
		if (allModifiers.some((modifier) => modifier.total_new_price != null)) {
			return allModifiers.reduce((sum, modifier) => sum + toPrice(modifier.total_new_price), 0);
		}
	}

	return getPaidModifiersTotal(null, item.modifiers ?? [], priceKey) + getGroupsPaidTotal(item.group_modifiers, undefined, priceKey);
};

export const getCartItemModifierBreakdowns = (
	item: CartModifiersInput | null | undefined,
	priceKey: ModifierPriceKey = "price",
): Map<string, FreeModifierBreakdown> => {
	const breakdowns = new Map<string, FreeModifierBreakdown>();
	if (!item) return breakdowns;

	for (const breakdown of allocateFreeModifiers(null, item.modifiers ?? [], priceKey)) {
		breakdowns.set(breakdown.id, breakdown);
	}

	for (const group of item.group_modifiers ?? []) {
		for (const breakdown of allocateFreeModifiers(resolveGroupFreeQuantity(group), group.modifiers ?? [], priceKey)) {
			breakdowns.set(breakdown.id, breakdown);
		}
	}

	return breakdowns;
};
